import React from 'react'
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Chip, Grid, Card, CardContent } from '@material-ui/core';
import WorkIcon from '@material-ui/icons/Work';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import BusinessIcon from '@material-ui/icons/Business';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  section: {
    minHeight: '100vh',
    padding: theme.spacing(6, 0),
    background: `
      radial-gradient(circle at 20% 50%, rgba(0, 245, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(199, 125, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(57, 255, 20, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, #0a0a0f 0%, #151521 50%, #1a1a2e 100%)
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 0),
      background: `linear-gradient(135deg, #0a0a0f 0%, #151521 50%, #1a1a2e 100%)`,
    },
    [theme.breakpoints.down('sm')]: {
      background: `linear-gradient(135deg, #0a0a0f 0%, #151521 100%)`,
      minHeight: 'auto',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(45deg, rgba(0, 245, 255, 0.03), rgba(199, 125, 255, 0.03))',
      zIndex: 1,
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing(0, 2),
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 1),
    },
  },
  titleHeader: {
    textAlign: 'center',
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 700,
    fontSize: '3.2rem',
    background: 'linear-gradient(135deg, #00f5ff, #39ff14, #c77dff)',
    backgroundSize: '300% 300%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(2),
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      animation: 'gradientShift 4s ease infinite',
    },
    '&::after': {
      content: '"💼"',
      position: 'absolute',
      top: '-15px',
      right: '-50px',
      fontSize: '2rem',
      opacity: 0.8,
    },
    '&::before': {
      content: '"🚀"',
      position: 'absolute',
      top: '-15px',
      left: '-50px',
      fontSize: '2rem',
      opacity: 0.8,
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
      '&::after, &::before': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      marginBottom: theme.spacing(1),
      background: '#00f5ff',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
  },
  subtitle: {
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '1.2rem',
    fontFamily: '"Inter", sans-serif',
    marginBottom: theme.spacing(6),
    maxWidth: '600px',
    margin: '0 auto',
    marginBottom: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      fontSize: '1.1rem',
      marginBottom: theme.spacing(4),
      padding: theme.spacing(0, 2),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(3),
      padding: theme.spacing(0, 1),
    },
  },
  experienceCard: {
    background: 'rgba(10, 15, 30, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(0, 245, 255, 0.2)',
    borderRadius: '20px',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2.5),
      borderRadius: '16px',
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      borderRadius: '12px',
      marginBottom: theme.spacing(2),
      marginLeft: theme.spacing(2),
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'linear-gradient(90deg, transparent, var(--company-color, #00f5ff), transparent)',
      [theme.breakpoints.up('md')]: {
        animation: 'shimmer 3s ease-in-out infinite',
      },
    },
    '&:hover': {
      [theme.breakpoints.up('md')]: {
        transform: 'translateY(-8px) scale(1.02)',
        borderColor: 'var(--company-color, #00f5ff)',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3), 0 0 30px var(--company-glow, rgba(0, 245, 255, 0.3))',
        background: 'rgba(15, 20, 35, 0.9)',
      },
      [theme.breakpoints.down('md')]: {
        transform: 'translateY(-4px)',
        borderColor: 'var(--company-color, #00f5ff)',
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
      },
    },
  },
  timelineContainer: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(2),
      top: 0,
      bottom: 0,
      width: '2px',
      background: 'linear-gradient(180deg, #00f5ff, #c77dff, #ff6b35, #ffd23f, #4ecdc4, #00d4ff, #1e90ff, #0080ff, #4169e1)',
      zIndex: 1,
      [theme.breakpoints.down('md')]: {
        left: theme.spacing(1.5),
        background: '#00f5ff',
      },
      [theme.breakpoints.down('sm')]: {
        left: theme.spacing(1),
        background: '#00f5ff',
      },
    },
  },
  timelineDot: {
    position: 'absolute',
    left: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'var(--company-color, #00f5ff)',
    border: '3px solid rgba(10, 15, 30, 1)',
    zIndex: 2,
    boxShadow: '0 0 20px var(--company-glow, rgba(0, 245, 255, 0.5))',
    [theme.breakpoints.down('md')]: {
      left: theme.spacing(0.5),
      width: '14px',
      height: '14px',
    },
    [theme.breakpoints.down('sm')]: {
      left: theme.spacing(0.25),
      width: '12px',
      height: '12px',
      border: '2px solid rgba(10, 15, 30, 1)',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
  },
  positionTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    fontFamily: '"JetBrains Mono", monospace',
    color: '#ffffff',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      fontSize: '1.3rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      lineHeight: 1.3,
    },
  },
  companyName: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--company-color, #00f5ff)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    filter: 'brightness(1.2) saturate(1.3)',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  periodChip: {
    background: 'linear-gradient(135deg, var(--company-color, #00f5ff), var(--company-secondary, #c77dff))',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: '"Inter", sans-serif',
    border: 'none',
    alignSelf: 'flex-start',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    [theme.breakpoints.down('md')]: {
      alignSelf: 'flex-start',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
      '& .MuiChip-icon': {
        fontSize: '1rem',
      },
    },
    '& .MuiChip-label': {
      padding: '8px 16px',
      [theme.breakpoints.down('sm')]: {
        padding: '6px 12px',
      },
    },
  },
  locationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.85rem',
      marginBottom: theme.spacing(1.5),
    },
  },
  achievementsList: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
    },
    '& li': {
      color: 'rgba(255, 255, 255, 0.9)',
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
      fontSize: '0.95rem',
      fontFamily: '"Inter", sans-serif',
      listStyle: 'none',
      position: 'relative',
      paddingLeft: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.9rem',
        marginBottom: theme.spacing(0.8),
        paddingLeft: theme.spacing(2.5),
        lineHeight: 1.5,
      },
      '&::before': {
        content: '"▸"',
        position: 'absolute',
        left: 0,
        color: 'var(--company-color, #00f5ff)',
        fontWeight: 'bold',
      },
    },
  },
  techStack: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(1.5),
    },
  },
  techChip: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '0.8rem',
    fontFamily: '"Inter", sans-serif',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      height: '28px',
    },
    '&:hover': {
      background: 'var(--company-color, #00f5ff)',
      color: '#000',
      transform: 'scale(1.05)',
    },
  },
  expandButton: {
    color: 'var(--company-color, #00f5ff)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 245, 255, 0.1)',
    },
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(6),
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(3),
    },
  },
  statItem: {
    textAlign: 'center',
    background: 'rgba(10, 15, 30, 0.6)',
    backdropFilter: 'blur(15px)',
    padding: theme.spacing(2, 3),
    borderRadius: '15px',
    border: '1px solid rgba(0, 245, 255, 0.2)',
    minWidth: '120px',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5, 2.5),
      minWidth: '100px',
      borderRadius: '12px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 2),
      minWidth: '80px',
      borderRadius: '10px',
      flex: '1 1 auto',
      maxWidth: '140px',
    },
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    fontFamily: '"JetBrains Mono", monospace',
    background: 'linear-gradient(135deg, #00f5ff, #39ff14)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.5rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.7rem',
      minHeight: '2.2rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.4rem',
      minHeight: '2rem',
    },
  },
  statLabel: {
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.6)',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    [theme.breakpoints.down('md')]: {
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
      letterSpacing: '0.3px',
    },
  },
  currentRole: {
    '&::after': {
      content: '"CURRENT"',
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
      background: 'linear-gradient(135deg, #39ff14, #00f5ff)',
      color: '#000',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: 'bold',
      fontFamily: '"Inter", sans-serif',
      letterSpacing: '1px',
      animation: 'pulse 2s ease-in-out infinite',
      [theme.breakpoints.down('sm')]: {
        top: theme.spacing(1),
        right: theme.spacing(1),
        padding: '3px 8px',
        fontSize: '0.6rem',
        borderRadius: '8px',
      },
    },
  },
  // Animaciones
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
  '@keyframes backgroundShift': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.8 },
  },
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.7 },
    '100%': { opacity: 1 },
  },
}));

const WorkExperience = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const workExperienceData = [
    {
      id: 1,
      period: 'March 2021 - September 2023',
      position: 'Sr. Java Backend Developer',
      company: 'Lynk',
      location: 'Jamaica',
      type: 'Full-time • Remote',
      companyColor: '#00f5ff',
      companySecondary: '#c77dff',
      companyGlow: 'rgba(0, 245, 255, 0.3)',
    },
    {
      id: 2,
      period: 'August 2018 – March 2021',
      position: 'Sr. .NET Developer',
      company: 'GALICIA SEGUROS S.A',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#c77dff',
      companySecondary: '#e0aaff',
      companyGlow: 'rgba(199, 125, 255, 0.3)',
    },
    {
      id: 3,
      period: 'October 2017 – August 2018',
      position: 'Freelancer',
      company: 'Independent Consultant',
      location: 'Buenos Aires, Argentina',
      type: 'Freelance • Remote',
      companyColor: '#e0aaff',
      companySecondary: '#ff6b35',
      companyGlow: 'rgba(224, 170, 255, 0.3)',
    },
    {
      id: 4,
      period: 'April 2017 – October 2017',
      position: 'IT Project Manager',
      company: 'TARJETA SHOPPING S.A',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#ff6b35',
      companySecondary: '#ffd23f',
      companyGlow: 'rgba(255, 107, 53, 0.3)',
    },
    {
      id: 5,
      period: 'January 2016 – April 2017',
      position: 'IT Project Manager',
      company: 'ISBAN (SANTANDER GROUP S.A)',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#ffd23f',
      companySecondary: '#4ecdc4',
      companyGlow: 'rgba(255, 210, 63, 0.3)',
    },
    {
      id: 6,
      period: 'November 2014 - January 2016',
      position: 'Sr. .NET Developer',
      company: 'ISBAN (SANTANDER GROUP S.A)',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#00d4ff',
      companySecondary: '#4ecdc4',
      companyGlow: 'rgba(0, 212, 255, 0.3)',
    },
    {
      id: 7,
      period: 'April 2013 - November 2014',
      position: 'Sr. .NET Developer',
      company: 'BANCO HIPOTECARIO S.A',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#1e90ff',
      companySecondary: '#00bfff',
      companyGlow: 'rgba(30, 144, 255, 0.3)',
    },
    {
      id: 8,
      period: 'July 2012 – April 2013',
      position: '.NET Developer',
      company: 'SYNCROMIND S.R.L',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#0080ff',
      companySecondary: '#4169e1',
      companyGlow: 'rgba(0, 128, 255, 0.3)',
    },
    {
      id: 9,
      period: 'December 2010 - July 2012',
      position: 'UV BASIC & Shellscript Developer',
      company: 'Telecom',
      location: 'Buenos Aires, Argentina',
      type: 'Full-time • On-site',
      companyColor: '#4169e1',
      companySecondary: '#6a5acd',
      companyGlow: 'rgba(65, 105, 225, 0.3)',
    },
  ];

  // Calcular estadísticas
  const totalExperience = Math.floor((new Date() - new Date('2010-12-01')) / (365.25 * 24 * 60 * 60 * 1000));
  return (
    <section className={classes.section}>
      <div className={classes.container}>
        <Typography variant="h1" className={classes.titleHeader}>
          {t('experience.title')}
        </Typography>
        
        <Typography className={classes.subtitle}>
          {t('experience.subtitle', { years: totalExperience })}
        </Typography>

        {/* Estadísticas */}
        <div className={classes.statsContainer}>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>{totalExperience}+</div>
            <div className={classes.statLabel}>{t('experience.stats.yearsExperience')}</div>
          </div>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>∞</div>
            <div className={classes.statLabel}>{t('experience.stats.coffeeConsumed')}</div>
          </div>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>∞</div>
            <div className={classes.statLabel}>{t('experience.stats.passion')}</div>
          </div>
          <div className={classes.statItem}>
            <div className={classes.statNumber}>∞</div>
            <div className={classes.statLabel}>{t('experience.stats.creativity')}</div>
          </div>
        </div>

        {/* Experiencias con Timeline */}
        <div className={classes.timelineContainer}>
          <Grid container spacing={3}>
            {workExperienceData.map((job) => (
              <Grid item xs={12} key={job.id}>
                <Card 
                  className={classes.experienceCard}
                  style={{
                    '--company-color': job.companyColor,
                    '--company-secondary': job.companySecondary,
                    '--company-glow': job.companyGlow,
                  }}
                >
                  <div 
                    className={classes.timelineDot}
                    style={{
                      background: job.companyColor,
                      boxShadow: `0 0 20px ${job.companyGlow}`,
                    }}
                  ></div>
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <div>
                        <Typography className={classes.positionTitle}>
                          {job.position}
                        </Typography>
                        <div className={classes.companyName}>
                          <BusinessIcon fontSize="small" />
                          {job.company}
                        </div>
                        <div className={classes.locationInfo}>
                          <LocationOnIcon fontSize="small" />
                          {job.location} • {job.type}
                        </div>
                      </div>
                      <Chip 
                        label={job.period} 
                        className={classes.periodChip}
                        icon={<WorkIcon />}
                      />
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
    </section>
  );
};

export default WorkExperience
